"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { Loader2, PenSquare } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { forumService, FeedSort, forumPath } from "./ForumService";
import { PostCard } from "./PostCard";
import { SortTabs, CategoryChips, PostFeedSkeleton, ForumEmptyState, NewPostsPill } from "./ForumChrome";
import { useForumAccess } from "../hooks/useForumAccess";

// The feed: Hot/New/Top infinite scroll, pinned threads leading page 1, a
// New-threads pill fed by the 60s latest-poll. Rows, not cards.
export function PostFeed({
  categorySlug,
  categoryName,
  title,
}: {
  categorySlug?: string | null;
  categoryName?: string;
  title?: string;
}) {
  const [sort, setSort] = useState<FeedSort>("hot");
  const router = useRouter();
  const queryClient = useQueryClient();
  const { requireWrite } = useForumAccess();
  const sentinelRef = useRef<HTMLDivElement>(null);

  const query = useInfiniteQuery({
    queryKey: ["forum", "posts", { sort, category: categorySlug || null }],
    queryFn: ({ pageParam }) =>
      forumService.getFeed({ sort, category: categorySlug || null, cursor: pageParam as string | null }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : undefined),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // The page-1 fresh slot can reappear at its natural rank on a later page — keep the first.
  const posts = useMemo(() => {
    const seen = new Set<string>();
    return (query.data?.pages || []).flatMap((p) => p.posts).filter((p) => (seen.has(p.id) ? false : (seen.add(p.id), true)));
  }, [query.data]);
  const pinned = query.data?.pages?.[0]?.pinned || [];
  const newestLoaded = useMemo(() => {
    const all = [...pinned, ...posts];
    return all.length ? all.reduce((m, p) => (p.createdAt > m ? p.createdAt : m), all[0].createdAt) : null;
  }, [posts, pinned]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
          query.fetchNextPage();
        }
      },
      { rootMargin: "600px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  useEffect(() => {
    try {
      localStorage.setItem("forum-last-seen", new Date().toISOString());
    } catch {}
  }, []);

  const refreshFeed = () => {
    queryClient.resetQueries({ queryKey: ["forum", "posts", { sort, category: categorySlug || null }] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const heading = title || (sort === "new" ? "Newest" : sort === "top" ? "Top all time" : "On the floor now");

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 pb-3">
        <h2 className="forum-serif text-[18px] font-medium text-foreground">{heading}</h2>
        <SortTabs sort={sort} onChange={setSort} />
      </div>

      <CategoryChips activeSlug={categorySlug} />
      <NewPostsPill newestLoaded={newestLoaded} onRefresh={refreshFeed} />

      {query.isLoading ? (
        <PostFeedSkeleton />
      ) : query.isError ? (
        <ForumEmptyState
          title="Couldn't load the floor"
          subtitle={(query.error as Error)?.message}
          action={
            <Button size="sm" variant="outline" onClick={() => query.refetch()}>
              Try again
            </Button>
          }
        />
      ) : posts.length === 0 && pinned.length === 0 ? (
        <ForumEmptyState
          title={categoryName ? `Nothing in ${categoryName} yet.` : "The floor is open. No threads yet."}
          subtitle="The first one is yours — a case that needs a carrier, a number you're proud of, a vendor that answered."
          action={
            <Button size="sm" onClick={() => requireWrite(() => router.push(forumPath(`/submit${categorySlug ? `?category=${categorySlug}` : ""}`)))}>
              <PenSquare className="mr-1.5 h-3.5 w-3.5" />
              Post the first thread
            </Button>
          }
        />
      ) : (
        <div>
          <div className="divide-y rounded-lg border bg-card">
            {pinned.map((post) => (
              <PostCard key={`pin-${post.id}`} post={post} hideCategory={!!categorySlug} />
            ))}
            {posts
              .filter((p) => !pinned.some((pin) => pin.id === p.id))
              .map((post) => (
                <PostCard key={post.id} post={post} hideCategory={!!categorySlug} />
              ))}
          </div>
          <div ref={sentinelRef} />
          {query.isFetchingNextPage && (
            <div className="flex justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {!query.hasNextPage && posts.length > 10 && (
            <p className="py-4 text-center font-mono text-[11px] text-muted-foreground/70">End of the floor.</p>
          )}
        </div>
      )}
    </div>
  );
}
